import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, MoreVertical, Copy, Trash2, Eye, Loader2, Plus, FolderOpen } from "lucide-react";

export default function CatalogueList({ status, title }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/catalogues", { params: { status, search: search || undefined } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [status, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/catalogues/${c.id}/duplicate`);
      toast.success(`Duplicated as ${data.catalogue_number}`);
      navigate(`/catalogue/${data.id}`);
    } catch (e) {
      toast.error("Could not duplicate");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/catalogues/${toDelete.id}`);
      toast.success("Catalogue deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="pgt-fade-up space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">{STATUS_META[status]?.label}</div>
          <h1 className="font-display text-4xl text-[#0B3B2E]">{title}</h1>
        </div>
        <Button onClick={() => navigate("/create")} className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2">
          <Plus className="h-4 w-4" /> New Catalogue
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#14513D]/50" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search catalogue number…" className="pl-10" data-testid="list-search" />
      </div>

      {rows === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0B3B2E]" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-16 text-center">
          <FolderOpen className="h-10 w-10 mx-auto text-[#C9A34A]/60" />
          <div className="mt-3 text-[#14513D]/70">No {STATUS_META[status]?.label.toLowerCase()} catalogues yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((c) => (
            <Card key={c.id} data-testid={`cat-card-${c.catalogue_number}`} className="p-5 border-[#C9A34A]/25 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start justify-between">
                <div className="font-mono-id text-sm font-bold text-[#C9A34A]">{c.catalogue_number}</div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-7 w-7 -mt-1 -mr-1" data-testid={`cat-menu-${c.catalogue_number}`}>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/catalogue/${c.id}`)}><Eye className="h-4 w-4 mr-2" /> Open</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicate(c)}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setToDelete(c)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h3 className="mt-2 font-display text-xl text-[#0B3B2E] leading-tight">{c.title || "Untitled Catalogue"}</h3>
              <div className="mt-1 text-sm text-[#14513D]/70">
                {c.customer_name || "No customer"} · {(c.products || []).length} products
              </div>
              <div className="mt-4 flex items-center justify-between pt-4 border-t border-[#C9A34A]/15">
                <Badge variant="outline" className={STATUS_META[c.status]?.cls}>{STATUS_META[c.status]?.label}</Badge>
                <Button size="sm" variant="outline" onClick={() => navigate(`/catalogue/${c.id}`)} data-testid={`cat-open-${c.catalogue_number}`}>Open</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete catalogue {toDelete?.catalogue_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the catalogue from your workspace. The catalogue number is retained and will never be reused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
